/**
 * Who may set a booking to what — the rules behind the Set Status dropdown.
 *
 * A sibling of `coachRoster.js` rather than part of it: that module answers
 * *which screen somebody gets*, this one answers *what a write may say*. The two
 * are genuinely different questions, and conflating them is how a read-only
 * concern ends up gating a write.
 *
 * Everything here mirrors `validate_booking_authority` and
 * `validate_status_timing` in the backend's `booking_validations.py`. The
 * authority matrix, quoted so the two can be diffed:
 *
 *   _MEMBER_STATUSES    = {None, 'confirmed', 'rescheduled', 'cancelled'}
 *   _COACH_STATUSES     = {'no show', 'completed'}
 *   _MANAGER_STATUSES   = {None, 'cancelled', 'no show', 'completed'}
 *   _EXECUTIVE_STATUSES = {None, 'confirmed', 'rescheduled', 'cancelled',
 *                          'no show', 'completed'}
 *
 * The member set applies only to a caller with no employee row — staff get no
 * member standing. The manager set needs a live `Manager`-titled role, and
 * applies on a direct reportee's calendar **or the caller's own**; a reporting
 * line without the title grants nothing.
 *
 * Two consequences worth naming because they read as bugs otherwise. A coach may
 * mark attendance and nothing else — calling off or moving a member's call is
 * not theirs. And a manager may book and *correct* the record but not confirm
 * or reschedule by UPDATE, which is why `'confirmed'` and `'rescheduled'` are
 * absent from their set (an UPDATE to Rescheduled is refused for everyone
 * anyway; a reschedule is a cancel plus a Rescheduled ADD).
 *
 * **None of this is a security boundary.** Every write is authorised
 * server-side, and `wrapCommsError` carries the API's own 403/400 wording
 * through to the toast, so a gate here that disagrees produces a clear error
 * rather than a wrong outcome. What it buys is not offering an action that is
 * certain to fail.
 *
 * No React, no API — unit-testable in isolation.
 */

import { isExecutiveEmployee } from "../../../utils/accessControl";
import {
  STATUS,
  callHasEnded,
  normaliseStatus,
  requiresEndedCall,
  statusIdFor,
  statusTitleFor,
} from "./bookingStatus";
import { isCoachManager } from "./coachRoster";

/**
 * What the product offers, in the order the dropdown lists them.
 *
 * **Cancelled is offered to executives only** — a product rule, narrower than
 * the API (which also lets members and managers cancel). Unlike the two
 * outcomes it is not held to the call having ended: calling a call off is
 * something you do before it happens. It is closed off once the call is
 * marked No Show or Completed. A booking that already reads Cancelled is
 * still closed to changes (`isClosedBooking`).
 */
const OFFERED = [STATUS.NO_SHOW, STATUS.COMPLETED, STATUS.CANCELLED];

/** What a coach may set: attendance, and only attendance. */
const COACH_TOKENS = [STATUS.NO_SHOW, STATUS.COMPLETED];

/**
 * What a manager may set — narrowed to what we offer, so the same two as a
 * coach. What sets them apart is not the list but the rules around it: no
 * once-only lock (`coachLockedOut`). Everyone, executives included, waits for
 * the call to end before setting either.
 */
const CORRECTIVE_TOKENS = [STATUS.NO_SHOW, STATUS.COMPLETED];

/** What an executive may set: the corrective pair, plus Cancelled. */
const EXECUTIVE_TOKENS = [...CORRECTIVE_TOKENS, STATUS.CANCELLED];

/** Statuses that mean the coach has already closed this call out. */
const CLOSED_OUT = new Set([STATUS.NO_SHOW, STATUS.COMPLETED]);

/**
 * Which authorities the viewer holds over one booking's slot.
 *
 * `managesSlot` is true in two shapes:
 *
 * 1. **Reading one of their coaches**, which the workspace already knows — a
 *    coach's roster row only exists if they report to the caller, so opening one
 *    *is* the reporting line. An executive browsing the whole-tenant roster also
 *    lands here over coaches they do not manage; harmless, since their own
 *    authority is a superset of a manager's.
 * 2. **On their own calendar, when they hold a coach-department Manager role.**
 *    Somebody who manages a coach department manages the record, including their
 *    own — being asked to "contact your manager" about your own call when you
 *    *are* the manager is nonsense.
 *
 * The second shape matches the API since `dca6c46`: `validate_booking_authority`
 * grants `_MANAGER_STATUSES` to a Manager-titled caller on their own calendar
 * as well as a direct reportee's. Dropping the once-only lock is safe — the
 * backend has no such rule. This shape is narrower than the server's (it needs a *coach-department* Manager
 * role, the server any `Manager` title), which only ever under-offers.
 *
 * `isExecutive` is FlexBoard's notion — a department titled `Executive` — which
 * is deliberately **not** the backend's `Employees.is_executive`. The two can
 * disagree, and when they do this over-offers and the API refuses; see the
 * module note above on why that is the acceptable direction to be wrong in.
 *
 * @param {Object} params
 * @param {Object|null|undefined} params.employeeData - ApiContext `employeeData`.
 * @param {Array|null|undefined} [params.departmentsWithRoles] - From
 *   `/get-common-utils`, the Roles.id → department index `isCoachManager` needs.
 *   Without it a manager on their own calendar falls back to coach rules.
 * @param {string|null|undefined} params.slotEmployeeId - `coach_id` on the
 *   booking row: the coach whose calendar it is.
 * @param {boolean} [params.viewingCoach] - The workspace is showing somebody
 *   else's calendar.
 * @returns {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }}
 */
export function resolveStatusAuthority({
  employeeData,
  departmentsWithRoles,
  slotEmployeeId,
  viewingCoach = false,
} = {}) {
  const callerEmployeeId =
    (employeeData?.data || employeeData || {}).employee_id ?? null;

  return {
    isExecutive: isExecutiveEmployee(employeeData),
    // Both ids must be present to match. Absent ids are not equal to each
    // other — treating two unknowns as the same person would hand a stranger
    // the coach's own authority.
    ownsSlot: Boolean(
      callerEmployeeId &&
        slotEmployeeId &&
        String(callerEmployeeId) === String(slotEmployeeId)
    ),
    managesSlot: Boolean(
      viewingCoach || isCoachManager(employeeData, departmentsWithRoles)
    ),
  };
}

/**
 * The tokens this authority may set at all, before the booking is considered.
 *
 * The sets accumulate, exactly as the backend's do, so somebody holding two
 * standings is judged as both. Coaches and managers yield the same two tokens;
 * only an executive adds Cancelled.
 *
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} authority
 * @returns {Set<string>}
 */
export function allowedTokens(authority) {
  const tokens = new Set();
  if (authority?.ownsSlot) COACH_TOKENS.forEach((t) => tokens.add(t));
  if (authority?.managesSlot) CORRECTIVE_TOKENS.forEach((t) => tokens.add(t));
  // Narrower than `_EXECUTIVE_STATUSES`, which would also permit 'confirmed'
  // and 'rescheduled'. Under-offering is safe.
  if (authority?.isExecutive) EXECUTIVE_TOKENS.forEach((t) => tokens.add(t));
  return tokens;
}

/**
 * Whether this viewer is a coach who has already had their one change.
 *
 * The product rule is "a coach sets the status once, then asks their manager".
 * There is no attribution column on a booking record, so "has already set it"
 * is inferred from the status *being* closed out — only a coach, a manager or an
 * executive can have written `No Show` or `Completed`.
 *
 * That inference has a useful side effect rather than a gap: a coach also reads
 * as locked when their *manager* set the status, which is the intent — a coach
 * should not be able to reverse a correction.
 *
 * Anyone with manager or executive standing is never locked.
 *
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} authority
 * @param {string} currentToken - Normalised current status.
 * @returns {boolean}
 */
export function coachLockedOut(authority, currentToken) {
  if (!authority?.ownsSlot) return false;
  if (authority.managesSlot || authority.isExecutive) return false;
  return CLOSED_OUT.has(currentToken);
}

/**
 * Whether a booking is closed to status changes altogether.
 *
 * A cancelled booking is closed **for everyone** (`validate_status_transition`:
 * "Booking {id} is cancelled and cannot be changed; book the slot again
 * instead" — a 409 with no executive exemption). Managers used to keep
 * No-show/Completed here as a correction; since `1f1b855` the server refuses
 * that, so offering it would only ever produce the error.
 */
function isClosedBooking(currentToken) {
  return currentToken === STATUS.CANCELLED;
}

/**
 * The dropdown's rows for one booking.
 *
 * Each row is `{ token, title, id, disabled, reason }`. A row that cannot be
 * chosen is returned **disabled with its reason** rather than omitted: "why
 * can't I pick Completed?" should answer itself, and an option that silently
 * vanishes is indistinguishable from one that was never permitted.
 *
 * `title` and `id` both come from the index, so the UI shows the backend's own
 * spelling and sends its own id rather than a second, drifting copy.
 *
 * Three exclusions, which drop a row entirely because there is nothing to
 * explain:
 *
 * - the status the booking already has — the server refuses it outright
 *   ("Booking {id} is already '{current}'", a 409);
 * - anything outside the viewer's authority;
 * - everything, when the booking is cancelled (closed for everyone) or the
 *   viewer is a locked-out coach — the latter gets copy instead of a dropdown.
 *
 * @param {Object} params
 * @param {{ isExecutive: boolean, ownsSlot: boolean, managesSlot: boolean }} params.authority
 * @param {Object} params.booking - A row carrying `booking_status` and `endsAt`/`slot_end`.
 * @param {Map<string, { id: string, title: string }>} params.statusIndex - From `buildStatusIndex`.
 * @param {Date} [params.now] - Injectable for tests and for the re-check at submit.
 * @returns {Array<{ token: string, title: string, id: string|null, disabled: boolean, reason: string|null }>}
 */
export function statusOptionsFor({ authority, booking, statusIndex, now } = {}) {
  const currentToken = normaliseStatus(booking?.booking_status);

  if (isClosedBooking(currentToken)) return [];
  if (coachLockedOut(authority, currentToken)) return [];

  const permitted = allowedTokens(authority);
  const ended = callHasEnded(booking, now);

  return OFFERED.filter(
    (token) => permitted.has(token) && token !== currentToken
  ).map((token) => {
    const id = statusIdFor(statusIndex, token);
    const title = statusTitleFor(statusIndex, token);

    // Order matters: a status we have no id for cannot be sent whatever the
    // clock says, so that reason wins.
    if (!id) {
      return {
        token,
        title,
        id: null,
        disabled: true,
        reason: "Not configured on this account — sign out and back in to refresh.",
      };
    }

    // Everyone is held to it, executives included — a product rule, narrower
    // than the API, which exempts executives. Under-offering is safe. "Their
    // authority is over whose calendar it is, not over when a call happened."
    if (requiresEndedCall(token) && !ended) {
      return {
        token,
        title,
        id,
        disabled: true,
        reason: `Can only be set once the call has ended.`,
      };
    }

    // A product rule with no server counterpart: once a call's outcome is on
    // record, it is not rewritten as never having happened.
    if (token === STATUS.CANCELLED && CLOSED_OUT.has(currentToken)) {
      return {
        token,
        title,
        id,
        disabled: true,
        reason: `Can't cancel a call already marked No Show or Completed.`,
      };
    }

    return { token, title, id, disabled: false, reason: null };
  });
}

/**
 * Whether the Set Status button is worth rendering at all for this booking.
 *
 * True when the viewer could choose *something* now, or when they are a
 * locked-out coach — the lock is worth surfacing, since "contact your manager"
 * is the answer to a question they are about to ask. False on a cancelled
 * booking (closed for everyone), and false when a write could not be built
 * anyway: the UPDATE targets the booking's `id`, and `clientKey` tolerates rows
 * missing one, so such a row can genuinely reach the agenda.
 *
 * @param {Object} params - As `statusOptionsFor`, which it delegates to.
 * @returns {boolean}
 */
export function canOpenStatusEditor({ authority, booking, statusIndex, now } = {}) {
  if (!booking?.id) return false;

  const currentToken = normaliseStatus(booking?.booking_status);
  if (isClosedBooking(currentToken)) return false;
  if (coachLockedOut(authority, currentToken)) return true;

  return (
    statusOptionsFor({ authority, booking, statusIndex, now }).length > 0
  );
}
